import { loadWebullPaperStatus, type WebullPaperStatus } from "./ownLedgerClient";

export type WebullBadgeStatus = "ok" | "warning" | "error" | "checking" | "unknown";

export interface WebullStatusResult {
  visible: boolean;
  badgeStatus: WebullBadgeStatus;
  connected: boolean;
  message: string;
}

const STATUS_POLL_MS = 60_000;
const STATUS_CHANGED = "sfe:webull-status-changed";

let latestResult: WebullStatusResult = {
  visible: false,
  badgeStatus: "unknown",
  connected: false,
  message: "Webull",
};

function accountCount(status: WebullPaperStatus): number {
  return Array.isArray(status.accounts) ? status.accounts.length : 0;
}

function syncNote(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  return ` · last sync ${new Date(ms).toLocaleString()}`;
}

function hiddenStatus(): WebullStatusResult {
  return {
    visible: false,
    badgeStatus: "unknown",
    connected: false,
    message: "Webull",
  };
}

export function deriveWebullStatus(status: WebullPaperStatus): WebullStatusResult {
  if (!status.connected) return hiddenStatus();
  const accounts = accountCount(status);
  if (accounts < 1) {
    return {
      visible: true,
      badgeStatus: "warning",
      connected: true,
      message: `Webull paper connected — no ledger accounts yet${syncNote(status.lastSyncAt)}`,
    };
  }
  const noun = accounts === 1 ? "account" : "accounts";
  return {
    visible: true,
    badgeStatus: "ok",
    connected: true,
    message: `Webull paper connected · ${accounts} ${noun}${syncNote(status.lastSyncAt)}`,
  };
}

export function getWebullStatusResult(): WebullStatusResult {
  return latestResult;
}

function setHoverTip(el: HTMLElement, tip: string): void {
  el.title = tip;
  el.setAttribute("data-tip", tip);
}

export function webullStatusIndicatorHtml(opts: { label?: string } = {}): string {
  const label = opts.label ?? "WB";
  return `
    <button type="button" class="sfe-wb-status" data-action="wb-status" hidden aria-label="Webull connection status" data-tip="Webull — checking…" title="Webull — checking…">
      <span class="sfe-wb-dot" data-wb-dot data-status="unknown" aria-hidden="true"></span>
      <span class="sfe-wb-status-label">${label}</span>
    </button>
  `;
}

export function applyWebullStatus(host: ParentNode, result: WebullStatusResult): void {
  latestResult = result;
  const btn = host.querySelector<HTMLElement>("[data-action='wb-status']");
  const dot = host.querySelector<HTMLElement>("[data-wb-dot]");
  if (!btn) return;
  btn.hidden = !result.visible;
  if (dot) dot.dataset.status = result.badgeStatus;
  setHoverTip(btn, result.message);
}

async function loadWebullStatus(): Promise<WebullStatusResult> {
  try {
    return deriveWebullStatus(await loadWebullPaperStatus());
  } catch (err) {
    const message = err instanceof Error ? err.message : "Could not read Webull status";
    if (/sign in required|unauthorized|\(40[13]\)/i.test(message)) return hiddenStatus();
    if (!latestResult.visible && !latestResult.connected) return hiddenStatus();
    return {
      visible: true,
      badgeStatus: "error",
      connected: false,
      message,
    };
  }
}

export function startWebullStatusPolling(onUpdate: (result: WebullStatusResult) => void): () => void {
  let cancelled = false;

  const run = async () => {
    if (cancelled) return;
    if (latestResult.visible) {
      onUpdate({
        ...latestResult,
        badgeStatus: "checking",
        message: "Checking Webull…",
      });
    }
    const result = await loadWebullStatus();
    if (!cancelled) onUpdate(result);
  };

  void run();
  const timer = window.setInterval(() => void run(), STATUS_POLL_MS);
  const onVisible = () => {
    if (document.visibilityState === "visible") void run();
  };
  document.addEventListener("visibilitychange", onVisible);
  const onChanged = () => void run();
  window.addEventListener(STATUS_CHANGED, onChanged);

  return () => {
    cancelled = true;
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener(STATUS_CHANGED, onChanged);
  };
}

export function notifyWebullStatusChanged(): void {
  window.dispatchEvent(new CustomEvent(STATUS_CHANGED));
}
